import React from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { getTranslationStatusOptions, Translation, TranslationStatus } from "../../model/Translation";
import { Box, IconButton, Tooltip } from "@mui/material";
import Icon from "../../components/ui/Icon";
import {
  getsylobProduct,
  getTranslationStatusColor,
  transformQuarterCodes,
} from "../../utils/ApiUtils/TranslationHelper";
import TranslationProgressBar from "../../components/translation/progress/TranslationLabelsProgressBar";
import { ErrorMessageCell } from "./CellRenders/ErrorMessageCell";
import { StatusCell } from "./CellRenders/StatusCell";

type HandleIntegrateTranslation = (translation: Translation) => void;
type HandleReExtractTranslation = (translation: Translation) => void;
type HandleReTranslate = (translation: Translation) => void;
type HandleDeleteTranslation = (translation: Translation) => void;
type HandleForceReviewStatus = (translation: Translation) => void;
type HandleContinueTranslate = (translation: Translation) => void;

export const getExportTranslationHiddenColumns = (): string[] => [
  "errorMessage",
  "reviewDate",
  "translationDate",
  "createdBy",
  "modifiedAt",
  "modifiedBy",
];
export const getExportTranslationColumns = (
  handleIntegrateTranslation: HandleIntegrateTranslation,
  handleReExtractTranslation: HandleReExtractTranslation,
  handleReTranslateTranslation: HandleReTranslate,
  handleDeleteTranslation: HandleDeleteTranslation,
  handleForceReviewStatus: HandleForceReviewStatus
): GridColDef[] => [
  {
    field: "product",
    headerName: "Product",
    hideable: false,
    width: 72,
    valueGetter: (value, row) => {
      return getsylobProduct(row.product);
    },
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const productName = getsylobProduct(params.row.product);
      return (
        <Tooltip title={productName}>
          <span>{productName}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "destinationLanguage",
    headerName: "Language",
    hideable: false,
    width: 85,
    valueGetter: (value) => (value === "en" ? "English" : value),
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const productLanguage = params.row.destinationLanguage === "en" ? "English" : params.row.destinationLanguage;
      return (
        <Tooltip title={params.row.destinationLanguage}>
          <span> {productLanguage}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "version",
    headerName: "Major version",
    hideable: false,
    width: 120,
    valueGetter: (value) => transformQuarterCodes(value),
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const productVersion = transformQuarterCodes(params.row.version);
      return (
        <Tooltip title={params.row.mavenVersion}>
          <span> {productVersion}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "mavenVersion",
    headerName: "Version",
    hideable: false,
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const mavenVersion = params.row.mavenVersion.replace("-SNAPSHOT", "");
      return (
        <Tooltip title={params.row.mavenVersion}>
          <span> {mavenVersion}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    hideable: false,
    type: "singleSelect",
    valueOptions: getTranslationStatusOptions(),
    minWidth: 150,
    renderCell: (params) => <StatusCell value={params.value} error={params.row.error} />,
  },
  {
    field: "dueDate",
    headerName: "Due Date",
    width: 120,
  },
  {
    field: "createdAt",
    headerName: "Extracted At",
    width: 100,
  },
  {
    field: "translationEndDate",
    headerName: "End date",
    width: 100,
  },
  {
    field: "reviewDate",
    headerName: "Review date",
  },
  {
    field: "translationDate",
    headerName: "translation start date",
  },
  {
    field: "createdBy",
    headerName: "Created by",
    flex: 1,
    maxWidth: 150,
  },
  {
    field: "modifiedBy",
    headerName: "Modified by",
    flex: 1,
    maxWidth: 150,
  },
  {
    field: "modifiedAt",
    headerName: "Modified At",
    flex: 1,
    maxWidth: 150,
  },
  {
    field: "errorMessage",
    headerName: "Error",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => <ErrorMessageCell value={params.value} />,
  },
  {
    field: "progress",
    headerName: "Progress",
    hideable: false,
    filterable: false,
    width: 160,
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const { row } = params;
      const initial = row.initialLabelsToTranslate;
      const remaining = row.nbLabelsToTranslate;
      return (
        <div className="flex items-center justify-start pt-2">
          {" "}
          <TranslationProgressBar initial={initial} pending={remaining} />
        </div>
      );
    },
  },
  {
    field: "actions",
    headerName: "Action",
    sortable: false,
    hideable: false,
    filterable: false,
    width: 250,
    renderCell: (params: GridRenderCellParams<Translation>) => {
      const { row } = params;
      const reExctractPossible = row.status !== TranslationStatus.EXTRACTION_FAILED;
      const integratePossible =
        row.status === TranslationStatus.REVIEW_COMPLETED || row.status === TranslationStatus.INTEGRATION_FAILED;
      const deleteImpossibble =
        row.status === TranslationStatus.EXTRACTION_RUNNING || row.status === TranslationStatus.INTEGRATION_RUNNING;
      const forceStatusImpossible =
        row.status === TranslationStatus.EXTRACTION_RUNNING ||
        row.status === TranslationStatus.INTEGRATION_RUNNING ||
        row.status === TranslationStatus.EXTRACTION_FAILED ||
        row.status === TranslationStatus.INTEGRATED ||
        row.status === TranslationStatus.EXTRACTED;
      const productName = params.row.product === "5" ? "Fortee" : "Sylob 9";
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={reExctractPossible ? "Cannot extract this translation" : "Re extract this translation"}>
            <span>
              <IconButton
                size="small"
                onClick={() => handleReExtractTranslation(row)}
                disabled={reExctractPossible}
                color="primary"
              >
                <Icon icon="rotate" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              !integratePossible ? "Cannot integrate this translation" : "Integrate this translation of " + productName
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={() => handleIntegrateTranslation(row)}
                disabled={!integratePossible}
                color="primary"
              >
                <Icon icon="upload" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={row.status === TranslationStatus.INTEGRATED ? "Continue translation" : "Cannot continue translation"}
          >
            <span>
              <IconButton
                size="small"
                onClick={() => handleReTranslateTranslation(row)}
                disabled={row.status !== TranslationStatus.INTEGRATED}
                color="primary"
              >
                <Icon icon="translate" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={!deleteImpossibble ? "Delete translation" : "Cannot delete translation"}>
            <span>
              <IconButton
                size="small"
                onClick={() => handleDeleteTranslation(row)}
                disabled={deleteImpossibble}
                color="error"
              >
                <Icon icon="delete" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={forceStatusImpossible ? "Cannot force to reviewed status" : "Force to reviewed status "}>
            <span>
              <IconButton
                size="small"
                onClick={() => handleForceReviewStatus(row)}
                disabled={
                  row.status === TranslationStatus.EXTRACTION_RUNNING ||
                  row.status === TranslationStatus.INTEGRATION_RUNNING ||
                  row.status === TranslationStatus.EXTRACTION_FAILED ||
                  row.status === TranslationStatus.INTEGRATED ||
                  row.status === TranslationStatus.EXTRACTED
                }
                color="warning"
              >
                <Icon icon="magic" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
  },
];
